import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Linking, ScrollView, StyleSheet, Text, View, } from 'react-native';
import { FittedLogo } from '../components/FittedLogo';
import { jwtDecode } from 'jwt-decode';
import { ContentView } from '../components/ContentView';
import { Button } from '../components/Button';
import { LOGO_WIDTH, LOGO_HEIGHT } from '../components/consts';
import { useConfirmEmailMutation, useResendConfirmationMutation, } from '../services/abbiWayApi';
import { theme } from '../theme';
const PAGE_MAX_WIDTH = 520;
const CARD_MAX_WIDTH = 480;
const parseErrors = (err) => {
    const e = err;
    const data = e?.data;
    return data?.errors;
};
export const ConfirmEmailScreen = ({ route }) => {
    const hash = route.params?.hash ?? '';
    const decodedPayload = useMemo(() => {
        if (!hash) {
            return null;
        }
        try {
            return jwtDecode(hash);
        }
        catch {
            return null;
        }
    }, [hash]);
    const [phase, setPhase] = useState('loading');
    const [headline, setHeadline] = useState('One moment…');
    const [calendarLink, setCalendarLink] = useState('');
    const [resendFeedback, setResendFeedback] = useState(null);
    const [cooldownLeft, setCooldownLeft] = useState(0);
    const ranConfirmRef = useRef(false);
    const [confirmEmail] = useConfirmEmailMutation();
    const [resend, { isLoading: resendLoading }] = useResendConfirmationMutation();
    useEffect(() => {
        if (cooldownLeft <= 0) {
            return;
        }
        const t = setInterval(() => {
            setCooldownLeft(s => (s > 0 ? s - 1 : 0));
        }, 1000);
        return () => {
            clearInterval(t);
        };
    }, [cooldownLeft]);
    useEffect(() => {
        ranConfirmRef.current = false;
    }, [hash]);
    useEffect(() => {
        if (!decodedPayload) {
            setPhase('decodeError');
            setHeadline('This confirmation link could not be read. Return to the app to resend a confirmation email, or contact support.');
            return;
        }
        const decoded = decodedPayload;
        const reportSuccess = () => {
            setPhase('success');
            setHeadline(`Your email address is confirmed, ${decoded.fullName}.`);
            const link = `https://calendly.com/abbiswayinfo/30min?name=${encodeURIComponent(decoded.fullName || '')}&email=${encodeURIComponent(decoded.email)}&location=${encodeURIComponent(`1${decoded.phone}` || '')}`;
            setCalendarLink(link);
        };
        if (__DEV__ && decoded.fullName === 'Skip Confirm') {
            reportSuccess();
            return;
        }
        if (ranConfirmRef.current) {
            return;
        }
        ranConfirmRef.current = true;
        confirmEmail({ hash })
            .unwrap()
            .then(reportSuccess)
            .catch((error) => {
            const errors = parseErrors(error);
            const hashErr = errors?.hash;
            if (hashErr === 'expiredHash' || hashErr === 'invalidHash') {
                setPhase('recoverable');
                setHeadline(hashErr === 'expiredHash'
                    ? 'This confirmation link has expired.'
                    : 'This confirmation link is not valid.');
                setResendFeedback('You can send yourself a fresh link below—you do not need to create another account.');
                return;
            }
            const fbq = error;
            const status = fbq.status;
            if (status === 404) {
                setPhase('alreadyDone');
                setHeadline('This link is no longer active.');
                setResendFeedback('Your account may already be confirmed. Try signing in. If you still need a link, use Resend below.');
                return;
            }
            if (status === 'FETCH_ERROR' ||
                status === 'PARSING_ERROR' ||
                status === 'TIMEOUT_ERROR') {
                setPhase('networkError');
                setHeadline('Network error. Check your connection and try again.');
                return;
            }
            setPhase('recoverable');
            setHeadline('We could not confirm your email from this link.');
            setResendFeedback('Try requesting a new confirmation email below, or contact support if this keeps happening.');
        });
    }, [confirmEmail, decodedPayload, hash]);
    const onResend = useCallback(async () => {
        if (!decodedPayload?.email) {
            return;
        }
        setResendFeedback(null);
        if (cooldownLeft > 0) {
            setResendFeedback(`Please wait ${cooldownLeft} seconds before requesting another email.`);
            return;
        }
        try {
            await resend({ email: decodedPayload.email }).unwrap();
            setResendFeedback('If your account is still waiting on confirmation, we sent a new link. Check your inbox and spam or promotions folders.');
        }
        catch (err) {
            const errors = parseErrors(err);
            if (errors?.confirmationEmail === 'recentlySent') {
                const sec = parseInt(String(errors.retryAfterSeconds ?? ''), 10);
                if (Number.isFinite(sec) && sec > 0) {
                    setCooldownLeft(sec);
                    setResendFeedback(`Please wait ${sec} seconds before requesting another confirmation email.`);
                    return;
                }
            }
            if (errors?.email === 'alreadyConfirmed') {
                setPhase('alreadyDone');
                setHeadline('Already confirmed');
                setResendFeedback('This email is already confirmed. Try signing in with your password.');
                return;
            }
            if (errors?.email === 'pendingUserNotFound') {
                setResendFeedback('We could not find a pending signup for this address. You may need to sign up again.');
                return;
            }
            setResendFeedback('Could not resend right now. Try again in a few minutes.');
        }
    }, [cooldownLeft, decodedPayload, resend]);
    const showResend = decodedPayload &&
        (phase === 'recoverable' || phase === 'alreadyDone');
    const cardTitle = (() => {
        if (phase === 'success') {
            return 'You’re all set';
        }
        if (phase === 'loading') {
            return 'Confirming your email';
        }
        if (phase === 'recoverable' || phase === 'alreadyDone') {
            return 'Confirmation link';
        }
        return 'Email confirmation';
    })();
    return (<ContentView style={styles.outer}>
      <ScrollView contentContainerStyle={styles.scroll} keyboardShouldPersistTaps="handled">
        <View style={styles.pageColumn}>
          <FittedLogo style={styles.logo}/>
          <View style={styles.card}>
            <Text style={styles.cardEyebrow}>{cardTitle}</Text>
            <Text style={phase === 'loading' ? styles.leadCentered : styles.lead}>
              {headline}
            </Text>
            {phase === 'success' && calendarLink.length ? (<>
                <Text style={styles.body}>
                  Next, schedule a short free meeting with your coach to get
                  started.
                </Text>
                <View style={styles.actions}>
                  <Button title="Schedule your free meeting" onPress={() => {
                if (calendarLink.length > 0) {
                    Linking.openURL(calendarLink);
                }
            }}/>
                </View>
              </>) : null}
            {showResend ? (<View style={styles.resendSection}>
                {resendFeedback ? (<Text style={styles.detailText}>{resendFeedback}</Text>) : null}
                <Text style={styles.helperText}>
                  Send to: {decodedPayload.email}
                </Text>
                <Button title={resendLoading
                ? 'Sending…'
                : cooldownLeft > 0
                    ? `Resend in ${cooldownLeft}s`
                    : 'Resend confirmation email'} loading={resendLoading} disabled={resendLoading || cooldownLeft > 0} onPress={onResend}/>
              </View>) : null}
          </View>
        </View>
      </ScrollView>
    </ContentView>);
};
const styles = StyleSheet.create({
    outer: {
        flex: 1,
    },
    scroll: {
        flexGrow: 1,
        paddingHorizontal: 20,
        paddingTop: 28,
        paddingBottom: 40,
        alignItems: 'center',
    },
    pageColumn: {
        width: '100%',
        maxWidth: PAGE_MAX_WIDTH,
        alignItems: 'center',
    },
    logo: {
        width: LOGO_WIDTH,
        height: LOGO_HEIGHT,
        marginBottom: 24,
    },
    card: {
        width: '100%',
        maxWidth: CARD_MAX_WIDTH,
        alignSelf: 'center',
        backgroundColor: theme.colors.textOnPrimary,
        borderRadius: 12,
        paddingHorizontal: 24,
        paddingTop: 28,
        paddingBottom: 24,
        borderWidth: 1,
        borderColor: theme.colors.borderSoft,
        shadowColor: theme.colors.shadow,
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.06,
        shadowRadius: 8,
        elevation: 2,
    },
    cardEyebrow: {
        fontSize: 13,
        fontWeight: '600',
        color: theme.colors.textSecondary,
        textTransform: 'uppercase',
        letterSpacing: 0.6,
        marginBottom: 12,
        textAlign: 'center',
    },
    lead: {
        fontSize: 17,
        lineHeight: 26,
        marginBottom: 16,
        color: theme.colors.textTitle,
        textAlign: 'left',
        fontWeight: '500',
    },
    leadCentered: {
        fontSize: 16,
        lineHeight: 24,
        marginBottom: 8,
        color: theme.colors.textTitle,
        textAlign: 'center',
    },
    body: {
        fontSize: 15,
        lineHeight: 22,
        marginBottom: 8,
        color: theme.colors.textTitle,
        textAlign: 'left',
    },
    actions: {
        marginTop: 12,
        width: '100%',
    },
    resendSection: {
        marginTop: 8,
        width: '100%',
    },
    helperText: {
        fontSize: 14,
        lineHeight: 20,
        marginBottom: 12,
        color: theme.colors.textBody,
        textAlign: 'left',
    },
    detailText: {
        fontSize: 14,
        lineHeight: 21,
        marginBottom: 14,
        textAlign: 'left',
        color: theme.colors.textTitle,
    },
});
